import type { ApprovalPolicy } from '@clockwise/clients/approval-policies';
import { LifecycleStatus } from '@clockwise/shared';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Filter, GripVertical, Loader2, Plus, RotateCw, Trash2, Undo2, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ApprovalPolicyFormSheet } from '@/components/custom/approval-policy-form-sheet';
import { LifecycleStatusBadge } from '@/components/custom/lifecycle-status-badge';
import { RequirePermission } from '@/components/custom/require-permission';
import { SearchInput } from '@/components/custom/search-input';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { approvalPoliciesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import {
  filterByStatus,
  MergeInvariantError,
  mergeVisibleIntoFull,
  type StatusFilter,
} from './-setup.approval-policies.list-ordering';

const statusFilterSchema = z.enum(['active', 'inactive', 'all']);

const listSearch = z.object({
  q: z.string().optional(),
  status: statusFilterSchema.optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/approval-policies/')({
  validateSearch: listSearch,
  component: ApprovalPoliciesIndexPage,
});

const SEARCH_DEBOUNCE_MS = 250;
// Hard ceiling matches the reorder validator on the server. A tenant
// pushing past this needs the page redesigned, not paginated — reorder
// + paginate is a UX trap (drag across pages is impossible).
const HARD_LIMIT = 500;

function ApprovalPoliciesIndexPage() {
  return (
    <RequirePermission permission="approval_policy.view">
      <ApprovalPoliciesList />
    </RequirePermission>
  );
}

function ApprovalPoliciesList() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const q = search.q ?? '';
  // Default to 'active' so the list isn't cluttered with long-disabled
  // rules. The status filter is purely a DISPLAY filter — we still
  // fetch the full tenant policy set below so drag-reorder composes
  // with it (hidden rows are anchored to their visible predecessor on
  // submit). Search, on the other hand, narrows the server response to
  // a true subset, so drag stays disabled while search is non-empty.
  const status: StatusFilter = search.status ?? 'active';

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  // Debounced search term — typing N characters issues ONE query (not N)
  // and ONE navigate once the user pauses.
  const [qInput, setQInput] = useState(q);
  useEffect(() => {
    setQInput(q);
  }, [q]);
  useEffect(() => {
    if (qInput === q) return;
    const handle = window.setTimeout(() => {
      updateSearch({ q: qInput || undefined });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [qInput, q]);

  const queryArgs = {
    q: q || undefined,
    // Intentionally NOT filtered by isActive — the server's /reorder
    // endpoint rejects partial submissions, so we keep the full tenant
    // set in the query result and apply the active/inactive filter
    // client-side. That lets a user drag while the "Active" view is
    // selected: hidden inactive rows are anchored to their visible
    // predecessor when the new ordering is submitted (see
    // `mergeVisibleIntoFull`). One audit-log consequence: dragging
    // visible rows can shift hidden rows' sort_order positionally, so
    // the server may write audit entries for rows the user didn't
    // touch in the UI — this is intentional (sort_order is the
    // authoritative precedence) but called out so future readers of a
    // policy's history page aren't confused.
    isActive: undefined as boolean | undefined,
    // Always fetch in precedence order so what the user drags IS what
    // the matcher will walk. Single page — drag-reorder + pagination
    // don't compose.
    sortField: 'sortOrder' as const,
    sortDirection: 'asc' as const,
    limit: HARD_LIMIT,
    offset: 0,
  };

  const list = useQuery({
    queryKey: queryKeys.approvalPolicies.list(queryArgs),
    queryFn: () => approvalPoliciesApi.list(queryArgs),
    placeholderData: keepPreviousData,
  });

  // The unfiltered full tenant set in precedence order — the source of
  // truth for both display (after a client-side status filter) and the
  // optimistic reorder.
  const allPolicies = list.data?.data ?? [];
  const visiblePolicies = useMemo(() => filterByStatus(allPolicies, status), [allPolicies, status]);

  // Drag-safety gate. Three independent reasons we must not submit a
  // reorder right now:
  //   1. Search is active — the cached list is a SERVER-side subset and
  //      /reorder requires the full tenant set.
  //   2. The debounced search input hasn't caught up to the URL yet —
  //      the user may have just typed/cleared but the query hasn't
  //      refetched, so `allPolicies` is `keepPreviousData` from the
  //      previous (possibly filtered) shape.
  //   3. The list is fetching — `placeholderData` keeps a stale snapshot
  //      visible. Dragging during this window would build the optimistic
  //      ordering off the OLD list and the server would bounce it.
  // Status filter is NOT a reason — we anchor hidden rows in
  // `mergeVisibleIntoFull` and submit the full list.
  const canReorder = !q && qInput === q && !list.isFetching;

  // One sheet serves both add and edit — `editing` null means create.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<ApprovalPolicy | null>(null);
  const openCreate = () => {
    setEditing(null);
    setSheetOpen(true);
  };
  const openEdit = (policy: ApprovalPolicy) => {
    setEditing(policy);
    setSheetOpen(true);
  };

  return (
    <PageShell>
      <PageHeader
        title="Approval Policies"
        subtitle="First matching rule wins. Drag to reorder — the matcher walks top to bottom and stops at the first match."
        count={visiblePolicies.length}
      >
        <RequirePermission permission="approval_policy.edit" fallback={null}>
          <Button onClick={openCreate}>
            <Plus className="mr-2" /> New Policy
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput value={qInput} onValueChange={setQInput} placeholder="Search by name…" />
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'] })}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {list.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : list.isError ? (
        // Distinguish "we failed to load" from "the tenant has no
        // policies" — otherwise a transient fetch failure looks like
        // an empty state and the admin may try to create duplicates.
        <ListErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : visiblePolicies.length === 0 ? (
        <EmptyState hasPoliciesUnderOtherFilters={allPolicies.length > 0} onCreate={openCreate} />
      ) : (
        <SortablePolicyList
          allPolicies={allPolicies}
          status={status}
          queryArgs={queryArgs}
          canReorder={canReorder}
          onEdit={openEdit}
        />
      )}

      <ApprovalPolicyFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        policy={editing}
      />
    </PageShell>
  );
}

function ListErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  return (
    <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
      <div className="font-medium text-destructive">Couldn't load policies.</div>
      <div className="text-muted-foreground">{message}</div>
      <Button size="sm" variant="outline" onClick={onRetry}>
        <RotateCw className="mr-1.5 size-3.5" /> Retry
      </Button>
    </div>
  );
}

function EmptyState({
  hasPoliciesUnderOtherFilters,
  onCreate,
}: {
  /** True when the tenant has policies that just don't match the
   *  current filters — copy adapts so "Create one" doesn't appear next
   *  to a search/status filter that's hiding rows. */
  hasPoliciesUnderOtherFilters: boolean;
  onCreate: () => void;
}) {
  if (hasPoliciesUnderOtherFilters) {
    return (
      <div className="rounded-md border py-10 text-center text-sm text-muted-foreground">
        No policies match the current filter. Try changing the status filter or clearing the search.
      </div>
    );
  }
  return (
    <div className="rounded-md border py-10 text-center text-sm text-muted-foreground">
      No approval policies yet.{' '}
      <RequirePermission permission="approval_policy.edit" fallback={null}>
        <button type="button" className="text-primary underline" onClick={onCreate}>
          Create one
        </button>
      </RequirePermission>
    </div>
  );
}

interface SortableListProps {
  /** Full unfiltered tenant set in precedence order. Reorder requests
   *  MUST submit this full list — the server rejects partial submissions
   *  — so we keep it on hand to translate a visible-slice drag back into
   *  a full ordering on drop. */
  allPolicies: ApprovalPolicy[];
  /** Current status filter — needed to re-derive the visible slice from
   *  the optimistic full ordering during a drag, and to anchor hidden
   *  rows to their visible predecessor on submit. */
  status: StatusFilter;
  queryArgs: object;
  /** False when search is active, the debounced input is in transit, or
   *  the list is fetching — see the caller for the full reasoning. */
  canReorder: boolean;
  /** Opens the edit sheet for a row — passed down to each row's click handler. */
  onEdit: (policy: ApprovalPolicy) => void;
}

interface ReorderMutationVars {
  orderedIds: string[];
  /** Pre-drag snapshot captured at submit time. Used by `onError` to
   *  roll back to the EXACT state the user was looking at before they
   *  dragged — not to whatever `allPolicies` happens to be when the
   *  error lands (which can have advanced if a sibling mutation
   *  invalidated the list during the in-flight reorder). */
  rollback: ApprovalPolicy[];
}

function SortablePolicyList({
  allPolicies,
  status,
  queryArgs,
  canReorder,
  onEdit,
}: SortableListProps) {
  const queryClient = useQueryClient();
  // Optimistic mirror of the FULL ordered list. Display is the
  // status-filtered slice (`visibleItems` below); reorder requests
  // submit the full list because the server rejects partial reorders.
  const [fullItems, setFullItems] = useState<ApprovalPolicy[]>(allPolicies);

  const reorderMutation = useMutation({
    mutationFn: (vars: ReorderMutationVars) =>
      approvalPoliciesApi.reorder({ orderedIds: vars.orderedIds }),
    onSuccess: (res) => {
      // Replace the cache outright with the server's authoritative
      // ordering (server returns the full re-numbered list). The
      // allPolicies prop will change on next render and the effect
      // below will sync `fullItems` to it. Skipping `invalidateQueries`
      // avoids a refetch flash; the server's response IS the new state.
      queryClient.setQueryData(queryKeys.approvalPolicies.list(queryArgs), {
        data: res.data,
        meta: { total: res.data.length, limit: HARD_LIMIT, offset: 0 },
      });
      // Other queries that depend on policies still get invalidated so
      // they pick up the new sort_order on next mount.
      queryClient.invalidateQueries({
        queryKey: queryKeys.approvalPolicies.all,
        refetchType: 'none',
      });
    },
    onError: (e: Error, vars) => {
      // Roll back to the PRE-DRAG snapshot captured in `vars` (not the
      // current `allPolicies`, which a sibling mutation may have
      // advanced during the in-flight reorder).
      setFullItems(vars.rollback);
      toast.error(e.message);
    },
  });

  // Skip the prop→state sync while a reorder is in flight. Without
  // this, an incidental cache write (sibling invalidate, focus
  // refetch) lands the pre-drag ordering in `allPolicies`, the effect
  // fires `setFullItems(allPolicies)`, and the optimistic order yanks
  // back to its old position mid-mutation — then forward again when
  // onSuccess writes the server response. Gating on isPending keeps
  // the optimistic UI stable for the round-trip; onSuccess/onError
  // re-sync when they land (onError uses the captured rollback above).
  useEffect(() => {
    if (reorderMutation.isPending) return;
    setFullItems(allPolicies);
  }, [allPolicies, reorderMutation.isPending]);

  // Visible slice derived from the optimistic full list — what dnd-kit
  // sorts against. During a drag the user sees ONLY this slice move;
  // hidden rows reappear in their anchored positions once the server
  // round-trip lands.
  const visibleItems = useMemo(() => filterByStatus(fullItems, status), [fullItems, status]);
  const visibleIds = useMemo(() => visibleItems.map((p) => p.id), [visibleItems]);

  const sensors = useSensors(
    // Small distance threshold so a click on the row body (navigation)
    // doesn't get hijacked into a drag. 4px is the dnd-kit default that
    // feels right for desktop pointers — under that, the action is
    // unambiguously a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = visibleItems.findIndex((p) => p.id === active.id);
    const newIndex = visibleItems.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      // dnd-kit fired with ids that aren't in the current visible
      // slice — usually because a re-render (status change, refetch)
      // changed `visibleItems` between drag start and drop. Don't
      // silently no-op: a user who dropped a row deserves a signal.
      toast.error('Drag was interrupted — please try again.');
      return;
    }
    const nextVisible = arrayMove(visibleItems, oldIndex, newIndex);

    // Translate the visible-slice reorder back into a full ordering.
    // `mergeVisibleIntoFull` enforces a length invariant and throws
    // `MergeInvariantError` on caller-race bugs (e.g. visible row
    // dropped from `fullItems` between drag start and drop). We catch
    // it here so a client-side bug surfaces as a clear, distinct toast
    // instead of being routed through the server's generic
    // "refresh and try again" message — and we abort BEFORE setting
    // optimistic state or firing the mutation.
    let nextFull: ApprovalPolicy[];
    try {
      nextFull = mergeVisibleIntoFull(fullItems, nextVisible, status);
    } catch (err) {
      if (err instanceof MergeInvariantError) {
        toast.error('Reorder failed locally — refresh and try again.');
        return;
      }
      throw err;
    }

    setFullItems(nextFull);
    reorderMutation.mutate({
      orderedIds: nextFull.map((p) => p.id),
      // Snapshot for rollback — see ReorderMutationVars and onError.
      rollback: fullItems,
    });
  };

  const isSaving = reorderMutation.isPending;
  // Disable drag while a reorder is in flight — a second drag would
  // race the first and the optimistic state can get tangled. Also
  // matches the "rows are pending" visual: the spinner stays put while
  // the user can't move things further.
  const canDrag = canReorder && !isSaving;

  return (
    <div className="space-y-2">
      {/* No reserved height here when there's nothing to say — an always-
          rendered empty status row was adding a visible gap between the
          filters panel and the policy list that no other list page has. */}
      {(!canReorder && !isSaving) || isSaving ? (
        <div className="flex h-5 items-center text-xs text-muted-foreground">
          {!canReorder && !isSaving && (
            <span>
              Drag-reorder is disabled while a search is applied — clear the search to drag.
            </span>
          )}
          {isSaving && (
            <span className="flex items-center gap-1.5">
              <Loader2 className="size-3 animate-spin" /> Saving new order…
            </span>
          )}
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
          <ul className="surface-card divide-y rounded-md border">
            {visibleItems.map((policy) => (
              <SortablePolicyRow
                key={policy.id}
                policy={policy}
                draggable={canDrag}
                isSaving={isSaving}
                onEdit={onEdit}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortablePolicyRow({
  policy,
  draggable,
  isSaving,
  onEdit,
}: {
  policy: ApprovalPolicy;
  draggable: boolean;
  /** True while a reorder mutation is in flight. Replaces the displayed
   *  `sort_order` number with a spinner so the user doesn't read stale
   *  numbers in the moment between drop and the server's renumbered
   *  response landing. */
  isSaving: boolean;
  onEdit: (policy: ApprovalPolicy) => void;
}) {
  const queryClient = useQueryClient();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: policy.id,
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const deleteMutation = useMutation({
    mutationFn: () => approvalPoliciesApi.remove(policy.id),
    onSuccess: () => {
      toast.success('Policy deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.approvalPolicies.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const restoreMutation = useMutation({
    mutationFn: () => approvalPoliciesApi.restore(policy.id),
    onSuccess: () => {
      toast.success('Policy reactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.approvalPolicies.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const stepLabel = policy.autoApprove
    ? 'Auto'
    : `${policy.steps.length} step${policy.steps.length === 1 ? '' : 's'}`;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5',
        isDragging && 'relative z-10 bg-muted shadow-sm',
        !policy.isActive && 'opacity-60',
      )}
    >
      {/* Drag handle — separate from the row body so clicks on text
          still navigate. `touch-none` keeps mobile scroll from
          stealing the drag gesture. */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className={cn(
          'flex h-7 w-5 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing',
          !draggable && 'cursor-not-allowed opacity-30',
        )}
        aria-label="Drag to reorder"
        disabled={!draggable}
      >
        <GripVertical />
      </button>
      <span className="flex w-8 items-center justify-end text-xs num text-muted-foreground">
        {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : policy.sortOrder}
      </span>
      <button type="button" className="flex-1 text-left" onClick={() => onEdit(policy)}>
        <div className="text-sm font-medium">{policy.name}</div>
        {policy.description && (
          <div className="line-clamp-1 text-xs text-muted-foreground">{policy.description}</div>
        )}
      </button>
      <div className="flex items-center gap-2">
        {policy.autoApprove ? (
          <Badge variant="secondary" className="gap-1">
            <Zap className="size-3" /> Auto
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{stepLabel}</span>
        )}
        {/* Same emerald pill as the activities catalog, rate cards, and
            customer engagements — one "Active" look across the app. */}
        <LifecycleStatusBadge
          status={policy.isActive ? LifecycleStatus.Active : LifecycleStatus.Inactive}
        />
        <RequirePermission permission="approval_policy.edit" fallback={null}>
          {policy.isActive ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              aria-label="Deactivate policy"
            >
              <Trash2 className="size-3.5" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
              aria-label="Reactivate policy"
            >
              <Undo2 className="size-3.5" />
            </Button>
          )}
        </RequirePermission>
      </div>
    </li>
  );
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q) out.q = s.q;
  // 'active' is the default — only persist a non-default filter.
  if (s.status && s.status !== 'active') out.status = s.status;
  return out;
}
